import { SecondaryPageLink } from '@/PageManager'
import Image from '@/components/Image'
import { fetchEvent } from '@/services/client.service'
import { toNote } from '@/lib/link'
import { pointerFromATag, pointerFromETag } from '@/lib/tag'
import { cn } from '@/lib/utils'
import { HugeiconsIcon } from '@hugeicons/react'
import { ImageNotFound01Icon } from '@hugeicons/core-free-icons'
import type { AddressPointer, EventPointer } from '@nostr/tools/nip19'
import type { Event, NostrEvent } from '@nostr/tools/wasm'
import { useAsync } from 'react-use'

// NIP-58 kinds. NOTE: the bundled @nostr/tools still maps ProfileBadges to
// 30008 (the deprecated value), so the current 10008 value and the 30008
// badge-set kind are spelled out as literals.
export const BADGE_AWARD_KIND = 8
export const PROFILE_BADGES_KIND = 10008
export const BADGE_SET_KIND = 30008
export const BADGE_DEFINITION_KIND = 30009
/** Deprecated profile-badges marker: kind 30008 with `d` == this value. */
export const DEPRECATED_PROFILE_BADGES_D = 'profile_badges'

export type BadgeDefinitionMeta = {
  d?: string
  name?: string
  image?: string
  thumbs: string[]
  description?: string
}

export function parseBadgeDefinition(event: Event | NostrEvent): BadgeDefinitionMeta {
  const thumbs: string[] = []
  let d: string | undefined
  let name: string | undefined
  let image: string | undefined
  let description: string | undefined
  for (const tag of event.tags) {
    if (tag[0] === 'd' && d === undefined) d = tag[1]
    else if (tag[0] === 'name' && name === undefined) name = tag[1]
    else if (tag[0] === 'image' && image === undefined) image = tag[1]
    else if (tag[0] === 'description' && description === undefined) description = tag[1]
    else if (tag[0] === 'thumb' && tag[1]) thumbs.push(tag[1])
  }
  return { d, name, image, thumbs, description }
}

export function badgeDisplayName(meta: BadgeDefinitionMeta): string | undefined {
  return meta.name || meta.d
}

/** Prefer smallest thumb for grids, full image for detail views. */
export function pickBadgeImage(meta: BadgeDefinitionMeta, full = false): string | undefined {
  if (full) return meta.image || meta.thumbs[0]
  return meta.thumbs[0] || meta.image
}

export type BadgePair = {
  definition: AddressPointer
  award?: EventPointer
  relayHint?: string
}

/**
 * Ordered consecutive `a`/`e` pairs per NIP-58. A lone `a` without a
 * following `e` (and vice versa) is ignored, except `a` tags pointing at a
 * badge set (30008), which are returned as setRefs.
 */
export function parseBadgePairs(tags: string[][]): {
  pairs: BadgePair[]
  setRefs: AddressPointer[]
} {
  const pairs: BadgePair[] = []
  const setRefs: AddressPointer[] = []
  for (let i = 0; i < tags.length; i++) {
    const tag = tags[i]
    if (tag[0] === 'a' && tag[1]) {
      const next = tags[i + 1]
      if (next?.[0] === 'e' && next[1]) {
        const definition = pointerFromATag(tag)
        const award = pointerFromETag(next)
        if (definition) pairs.push({ definition, award, relayHint: tag[2] || next[2] })
        i++ // consume the `e`
      } else {
        const ptr = pointerFromATag(tag)
        if (ptr && ptr.kind === BADGE_SET_KIND) setRefs.push(ptr)
      }
    }
    // lone `e` tags are ignored
  }
  return { pairs, setRefs }
}

export function useBadgeDefinition(pointer?: AddressPointer) {
  return useAsync(async () => {
    if (!pointer) return undefined
    return fetchEvent(pointer)
  }, [pointer?.kind, pointer?.pubkey, pointer?.identifier])
}

export function BadgeThumb({
  meta,
  pubkey,
  size = 'md',
  className
}: {
  meta?: BadgeDefinitionMeta
  pubkey?: string
  size?: 'sm' | 'md' | 'lg'
  className?: string
}) {
  const src = meta ? pickBadgeImage(meta) : undefined
  const sizeCn = size === 'sm' ? 'size-10' : size === 'lg' ? 'size-24' : 'size-16'
  if (!src) {
    return (
      <div
        className={cn(
          'flex shrink-0 items-center justify-center rounded-lg border bg-muted text-muted-foreground',
          sizeCn,
          className
        )}
      >
        <HugeiconsIcon icon={ImageNotFound01Icon} size={20} />
      </div>
    )
  }
  return (
    <Image
      imageUrl={src}
      imagePubkey={pubkey}
      alt={meta ? badgeDisplayName(meta) : undefined}
      hideIfError
      classNames={{ wrapper: cn('shrink-0 rounded-lg border bg-muted', sizeCn, className) }}
    />
  )
}

/**
 * One badge cell: fetches definition (and award for link fallback),
 * renders thumb + name, links to the definition event.
 */
export function BadgeSlot({
  definition,
  award,
  size = 'md',
  showName = true,
  className
}: {
  definition: AddressPointer
  award?: EventPointer
  size?: 'sm' | 'md' | 'lg'
  showName?: boolean
  className?: string
}) {
  const { value: definitionEvent } = useBadgeDefinition(definition)
  const meta = definitionEvent ? parseBadgeDefinition(definitionEvent) : undefined
  const name = meta ? badgeDisplayName(meta) : undefined
  const to = toNote(definitionEvent ?? definition)

  return (
    <SecondaryPageLink
      to={to}
      onClick={(e) => e.stopPropagation()}
      className={cn('flex min-w-0 items-center gap-2 rounded-lg p-1 hover:bg-muted/60', className)}
    >
      <span
        title={name ?? `${definition.kind}:${definition.pubkey}:${definition.identifier}`}
        className="flex min-w-0 items-center gap-2"
      >
        <BadgeThumb meta={meta} pubkey={definition.pubkey} size={size} />
        {showName && (
          <span className="truncate text-sm font-medium">
            {name ?? (
              <span className="font-mono text-xs text-muted-foreground">
                {award ? award.id.slice(0, 12) + '…' : definition.identifier || '…'}
              </span>
            )}
          </span>
        )}
      </span>
    </SecondaryPageLink>
  )
}
